/**
 * MultiLanguageInput Component
 * 
 * A reusable component for managing multi-language content in admin forms.
 * Features:
 * - 5 language tabs (ES, EN, FR, IT, PT)
 * - Support for text input, textarea, or rich text editor
 * - AI-powered translation button using OpenAI
 * 
 * Usage:
 * <MultiLanguageInput
 *   baseField="title"
 *   values={{ title: 'Hola', title_en: 'Hello', ... }}
 *   onChange={(field, value) => updateState(field, value)}
 *   inputType="text" // or "textarea" or "rich"
 *   label="Page Title"
 * />
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import cx from 'classnames';
import { Check, Sparkles, CheckCircle, AlertTriangle, Loader2 } from 'lucide-react';

import s from './MultiLanguageInput.css';

// Language configuration - Solo 2 letras
const LANGUAGES = [
  { code: 'es', suffix: '', label: 'ES', name: 'Español' },
  { code: 'en', suffix: '_en', label: 'EN', name: 'English' },
  { code: 'fr', suffix: '_fr', label: 'FR', name: 'Français' },
  { code: 'it', suffix: '_it', label: 'IT', name: 'Italiano' },
  { code: 'pt', suffix: '_pt', label: 'PT', name: 'Português' },
];

class MultiLanguageInput extends Component {
  static propTypes = {
    baseField: PropTypes.string.isRequired,
    values: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
    inputType: PropTypes.oneOf(['text', 'textarea', 'rich']),
    label: PropTypes.string,
    placeholder: PropTypes.string,
    disabled: PropTypes.bool,
  };

  static defaultProps = {
    inputType: 'text',
    label: '',
    placeholder: '',
    disabled: false,
  };

  constructor(props) {
    super(props);
    this.state = {
      activeTab: 'es',
      isTranslating: false,
      translateSuccess: false,
      translateError: null,
    };
  }

  getFieldName = (langCode) => {
    const { baseField } = this.props;
    const lang = LANGUAGES.find(l => l.code === langCode);
    return `${baseField}${lang.suffix}`;
  };

  getValue = (langCode) => {
    const { values } = this.props;
    const fieldName = this.getFieldName(langCode);
    return values[fieldName] || '';
  };

  handleTabClick = (langCode) => {
    this.setState({ activeTab: langCode, translateSuccess: false, translateError: null });
  };

  handleInputChange = (e) => {
    const { onChange } = this.props;
    const { activeTab } = this.state;
    const fieldName = this.getFieldName(activeTab);
    onChange(fieldName, e.target.value);
  };

  handleRichChange = (value) => {
    const { onChange } = this.props;
    const { activeTab } = this.state;
    const fieldName = this.getFieldName(activeTab);
    onChange(fieldName, value);
  };

  handleTranslate = async () => {
    const { onChange, baseField, inputType } = this.props;
    const sourceText = this.getValue('es');

    if (!sourceText || sourceText.trim() === '') {
      this.setState({ 
        translateError: 'Please enter content in Spanish (ES) first',
        translateSuccess: false 
      });
      return;
    }

    this.setState({ isTranslating: true, translateError: null, translateSuccess: false });

    try {
      const isHtml = inputType === 'rich';
      
      const response = await fetch('/graphql', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          query: `
            mutation AutoTranslate($content: String!, $isHtml: Boolean) {
              AutoTranslate(content: $content, isHtml: $isHtml) {
                status
                errorMessage
                en
                fr
                it
                pt
              }
            }
          `,
          variables: {
            content: sourceText,
            isHtml
          }
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        throw new Error(result.errors[0]?.message || 'Translation failed');
      }

      const data = result.data?.AutoTranslate;
      
      if (data?.status !== 200) {
        throw new Error(data?.errorMessage || 'Translation failed');
      }

      // Update all language fields
      if (data.en) onChange(`${baseField}_en`, data.en);
      if (data.fr) onChange(`${baseField}_fr`, data.fr);
      if (data.it) onChange(`${baseField}_it`, data.it);
      if (data.pt) onChange(`${baseField}_pt`, data.pt);

      this.setState({ 
        translateSuccess: true, 
        isTranslating: false,
        translateError: null 
      });

      // Hide success message after 3 seconds
      setTimeout(() => {
        this.setState({ translateSuccess: false });
      }, 3000);

    } catch (error) {
      console.error('[MultiLanguageInput] Translation error:', error);
      this.setState({ 
        translateError: error.message || 'Translation failed. Please try again.',
        isTranslating: false,
        translateSuccess: false 
      });
    }
  };

  hasContent = (langCode) => {
    const value = this.getValue(langCode);
    return value && value.trim() !== '';
  };

  renderInput = () => {
    const { inputType, placeholder, disabled } = this.props;
    const { activeTab } = this.state;
    const value = this.getValue(activeTab);

    if (inputType === 'textarea') {
      return (
        <textarea
          className={cx(s.inputField, s.textareaField)}
          value={value}
          onChange={this.handleInputChange}
          placeholder={placeholder}
          disabled={disabled}
        />
      );
    }

    if (inputType === 'rich') {
      // For rich text, we'll use a simple textarea fallback
      // You can replace this with ReactQuill if needed
      return (
        <div className={s.richEditorContainer}>
          <textarea
            className={cx(s.inputField, s.textareaField)}
            value={value}
            onChange={this.handleInputChange}
            placeholder={placeholder}
            disabled={disabled}
            style={{ minHeight: '200px' }}
          />
        </div>
      );
    }

    // Default: text input
    return (
      <input
        type="text"
        className={s.inputField}
        value={value}
        onChange={this.handleInputChange}
        placeholder={placeholder}
        disabled={disabled}
      />
    );
  };

  render() {
    const { label, disabled } = this.props;
    const { activeTab, isTranslating, translateSuccess, translateError } = this.state;

    return (
      <div className={s.container}>
        <div className={s.header}>
          <div className={s.tabs}>
            {LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                type="button"
                className={cx(s.tab, { [s.tabActive]: activeTab === lang.code })}
                onClick={() => this.handleTabClick(lang.code)}
                title={lang.name}
              >
                <span>{lang.label}</span>
                {this.hasContent(lang.code) && (
                  <Check size={12} className={s.checkIcon} />
                )}
              </button>
            ))}
          </div>
          
          <button
            type="button"
            className={s.translateBtn}
            onClick={this.handleTranslate}
            disabled={isTranslating || disabled}
            title="Translate from Spanish to all languages"
          >
            {isTranslating ? (
              <>
                <Loader2 size={14} className={s.spinnerIcon} />
                <span>Translating...</span>
              </>
            ) : (
              <>
                <Sparkles size={14} />
                <span>AI Translate</span>
              </>
            )}
          </button>
        </div>

        <div className={s.content}>
          {label && (
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#495057' }}>
              {label}
              <span style={{ marginLeft: '8px', fontSize: '12px', color: '#6c757d' }}>
                ({LANGUAGES.find(l => l.code === activeTab)?.name})
              </span>
            </label>
          )}
          
          {this.renderInput()}

          {translateSuccess && (
            <div className={s.successMessage}>
              <CheckCircle size={16} />
              <span>All translations completed successfully!</span>
            </div>
          )}

          {translateError && (
            <div className={s.errorMessage}>
              <AlertTriangle size={16} />
              <span>{translateError}</span>
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default withStyles(s)(MultiLanguageInput);
